/**
 * Data retention contracts
 * Types and the support matrix shared by the backend executor/validators and the org-admin screens.
 */

export type RetentionDataType = 'transactions' | 'audit_logs' | 'imports' | 'reports' | 'sessions'
export type RetentionAction = 'delete' | 'archive' | 'anonymize'

/**
 * SINGLE SOURCE OF TRUTH for which retention actions the executor actually
 * performs per data type. The create validator, the update service, and the
 * executor all enforce this matrix so an org-admin can NEVER persist (or run)
 * an enabled policy whose action the executor would silently ignore, and the
 * Create/Edit forms only ever offer the actions listed here.
 *
 * Mirrors exactly what retention-executor.service.ts performs an action for:
 *   transactions: delete, archive, anonymize
 *   audit_logs:   delete, anonymize          (archive is a no-op → UNSUPPORTED)
 *   imports:      delete, archive, anonymize
 *   reports:      delete                       (archive/anonymize → UNSUPPORTED)
 *   sessions:     delete                       (archive/anonymize → UNSUPPORTED)
 */
export const SUPPORTED_RETENTION_ACTIONS: Record<RetentionDataType, readonly RetentionAction[]> = {
  transactions: ['delete', 'archive', 'anonymize'],
  audit_logs: ['delete', 'anonymize'],
  imports: ['delete', 'archive', 'anonymize'],
  reports: ['delete'],
  sessions: ['delete'],
}

export interface DataRetentionStatsResponse {
  totalPolicies: number
  activePolicies: number
  dataCounts: {
    transactions: number
    auditLogs: number
    imports: number
    reports: number
    sessions: number
  }
  oldestRecords: {
    transactions: string | null
    auditLogs: string | null
    imports: string | null
    reports: string | null
    sessions: string | null
  }
  retentionSummary: {
    dataType: string
    retentionDays: number
    action: string
    enabled: boolean
  }[]
}
